import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../styles';
import { Certification } from '../types';
import Button from 'modules/views/shared/Button';

interface ExpiredCertificateCardProps {
  certification: Certification;
  onUploadNew: (certification: Certification) => void;
}

export const ExpiredCertificateCard: React.FC<ExpiredCertificateCardProps> = ({ certification, onUploadNew }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '--') return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className={classes.expiredCertificateCard} data-testid={`expired-certificate-card-${certification?.id}`}>
      {/* Header */}
      <div className={classes.expiredCardHeader}>
        <div className={classes.certificateHeaderContent}>
          <div className={classes.certificationAvatar}>{getInitial(certification?.name || 'A')}</div>
          <div className={classes.certificationInfo}>
            <h3
              className={classes.certificationName}
              data-testid={`expired-certificate-card-name-${certification?.id}`}
            >
              {certification?.name || 'Ariel Lift Training Certificate'}
            </h3>
            <p className={classes.certificationDate}>{formatDate(certification?.lastUpdatedAt || certification?.issueDate)}</p>
          </div>
        </div>
      </div>

      {/* Certification Details */}
      <div className={classes.certificationDetails}>
        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('myCertifications.card.certificationId')}</span>
          <span className={classes.detailValue}>{certification?.certificationId || '--'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('myCertifications.card.description')}</span>
          <span className={classes.detailValue}>{certification?.description || '--'}</span>
        </div>

        <div className={classes.certificateDates}>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('myCertifications.card.issueDate')}</span>
            <span className={classes.detailValue}>{formatDate(certification?.issueDate)}</span>
          </div>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('myCertifications.card.expirationDate')}</span>
            <span className={classes.expiredDateValue}>{formatDate(certification?.expirationDate)}</span>
          </div>
        </div>

        <div className={classes.projectsRequired}>
          <p className={classes.projectsLabel}>{t('myCertifications.card.projectRequired')}</p>
          <p className={classes.projectsText}>{certification?.projectsRequired?.join?.(', ') || '--'}</p>
        </div>
      </div>

      {/* Upload New Button */}
      <Button
        label={t('myCertifications.card.uploadNew')}
        variant="outlined"
        color="primary"
        testId={`expired-certificate-upload-new-button-${certification?.id}`}
        onClick={() => onUploadNew(certification)}
        disabled={!!certification?.isNewCertificateAvailable}
        disableRipple
        styleClass={classes.uploadNewButton}
      />
    </div>
  );
};
